import { useEffect, useMemo, useState, useCallback } from "react";
import Layout, { PageHeader } from "../components/Layout";
import { api, formatApiError, money, fmtDateTime, fmtTime } from "../lib/api";
import { toast } from "sonner";
import CashOutDialog from "../components/CashOutDialog";
import CashInDialog from "../components/CashInDialog";
import OpenShiftDialog from "../components/OpenShiftDialog";
import MyCashOutsDrawer from "../components/MyCashOutsDrawer";
import { useAuth } from "../context/AuthContext";
import { userCan } from "../lib/permissions";
import {
  Wallet,
  ArrowDownRight,
  ArrowDownLeft,
  ArrowUpRight,
  Money,
  CreditCard,
  ClipboardText,
  Lock,
  LockOpen,
  Receipt,
  ListBullets,
  Printer,
  EnvelopeSimple,
  ChatCircleText,
  CaretRight,
  ClockCounterClockwise,
  Plus,
} from "@phosphor-icons/react";
import { Wallet as WalletIcon } from "@phosphor-icons/react";

// — Reusable mini-stat tile for the active-shift dashboard.
function StatTile({ label, value, accent, icon: Icon, testid, sub }) {
  return (
    <div
      data-testid={testid}
      className="rounded-2xl bg-white border border-[#E5E7EB] p-4"
      style={{ boxShadow: "0 2px 8px rgba(36,71,55,0.04)" }}
    >
      <div className="flex items-center gap-2 mb-2">
        {Icon && (
          <span className="w-7 h-7 rounded-lg bg-[#DBEAFE] flex items-center justify-center text-[#1E5FEB]">
            <Icon size={14} weight="bold" />
          </span>
        )}
        <div className="text-[10px] uppercase tracking-[0.16em] text-[#9CA3AF] font-medium">
          {label}
        </div>
      </div>
      <div
        className="font-display font-semibold text-[24px] tabular-nums"
        style={{ color: accent || "#121216" }}
      >
        ${money(value)}
      </div>
      {sub && (
        <div className="text-[10px] text-[#9CA3AF] mt-1 truncate" title={sub}>
          {sub}
        </div>
      )}
    </div>
  );
}

function Field({ label, children, hint }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[11px] uppercase tracking-[0.12em] text-[#4B5563] font-medium">
        {label}
      </span>
      {children}
      {hint && <span className="text-[11px] text-[#9CA3AF]">{hint}</span>}
    </label>
  );
}

// — End-shift / close-shift dialog
function CloseShiftDialog({ active, expectedCash, onClose, onClosed }) {
  const [counted, setCounted] = useState("");
  const [notes, setNotes] = useState("");
  const [printZ, setPrintZ] = useState(true);
  const [emailAdmin, setEmailAdmin] = useState(true);
  const [smsAdmin, setSmsAdmin] = useState(true);
  const [busy, setBusy] = useState(false);

  const variance = useMemo(() => {
    const n = Number(counted || 0) - Number(expectedCash || 0);
    return Math.round(n * 100) / 100;
  }, [counted, expectedCash]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post(`/shifts/${active.id}/close`, {
        counted_cash: Number(counted || 0),
        notes,
        print_z_report: printZ,
        email_admin: emailAdmin,
        sms_admin: smsAdmin,
      });
      const messages = [];
      if (r.data.print?.ok) messages.push("Z-report printed");
      else if (r.data.print?.skipped) messages.push("print skipped (no receipt printer set)");
      if (r.data.email?.ok) messages.push(`emailed ${r.data.email.to}`);
      if (r.data.sms?.ok) messages.push(`SMS to ${r.data.sms.to}`);
      toast.success(`Shift closed · variance $${money(r.data.variance)}${messages.length ? " · " + messages.join(" · ") : ""}`);
      onClosed?.(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" data-testid="close-shift-dialog">
      <div className="w-full max-w-md bg-white rounded-2xl overflow-hidden" style={{ boxShadow: "0 32px 80px rgba(0,0,0,0.18)" }}>
        <div className="px-6 py-5 border-b border-[#F3F4F6] flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#121216] text-white flex items-center justify-center shrink-0">
            <Lock size={16} weight="bold" />
          </div>
          <div>
            <div className="font-display font-semibold text-[18px]">End shift</div>
            <div className="text-[12px] text-[#9CA3AF] mt-0.5">Count the drawer, then close. We'll print a Z-report and notify the admin.</div>
          </div>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="rounded-xl bg-[#F8FAFC] border border-[#E5E7EB] p-4">
            <div className="text-[10px] uppercase tracking-[0.16em] text-[#9CA3AF] font-medium mb-1">Expected cash</div>
            <div className="font-display font-semibold text-[24px] tabular-nums" data-testid="close-shift-expected">
              ${money(expectedCash)}
            </div>
            <div className="text-[11px] text-[#4B5563] mt-1">Opening float + cash sales − refunds − cash outs.</div>
          </div>

          <Field label="Counted cash ($)">
            <input
              data-testid="close-shift-counted"
              type="number"
              step="0.01"
              min="0"
              value={counted}
              onChange={(e) => setCounted(e.target.value)}
              autoFocus
              className="h-11 w-full rounded-xl border border-[#E5E7EB] px-3 font-mono text-[15px] focus:border-[#1E5FEB] focus:outline-none"
              required
            />
          </Field>

          {counted !== "" && (
            <div
              data-testid="close-shift-variance"
              className={`rounded-xl px-4 py-3 text-[13px] font-semibold flex items-center justify-between ${
                Math.abs(variance) < 0.005
                  ? "bg-[#ECFDF5] text-[#0F9D58] border border-[#A7F3D0]"
                  : variance > 0
                  ? "bg-[#FEF3C7] text-[#92400E] border border-[#FCD34D]"
                  : "bg-[#FEE2E2] text-[#DC2626] border border-[#FCA5A5]"
              }`}
            >
              <span>Variance</span>
              <span className="tabular-nums font-mono">
                {variance >= 0 ? "+" : "−"}${money(Math.abs(variance))}
              </span>
            </div>
          )}

          <Field label="Notes (optional)">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="e.g. Found extra $5 under till"
              className="w-full rounded-xl border border-[#E5E7EB] px-3 py-2 text-[14px] focus:border-[#1E5FEB] focus:outline-none"
            />
          </Field>

          <div className="space-y-2 pt-1">
            <label className="flex items-center gap-2 text-[13px] cursor-pointer">
              <input type="checkbox" checked={printZ} onChange={(e) => setPrintZ(e.target.checked)} className="w-4 h-4" />
              <Printer size={14} weight="bold" className="text-[#4B5563]" />
              <span>Print Z-report on receipt printer</span>
            </label>
            <label className="flex items-center gap-2 text-[13px] cursor-pointer">
              <input type="checkbox" checked={emailAdmin} onChange={(e) => setEmailAdmin(e.target.checked)} className="w-4 h-4" />
              <EnvelopeSimple size={14} weight="bold" className="text-[#4B5563]" />
              <span>Email Z-report to admin</span>
            </label>
            <label className="flex items-center gap-2 text-[13px] cursor-pointer">
              <input type="checkbox" checked={smsAdmin} onChange={(e) => setSmsAdmin(e.target.checked)} className="w-4 h-4" />
              <ChatCircleText size={14} weight="bold" className="text-[#4B5563]" />
              <span>SMS summary to admin</span>
            </label>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="h-11 px-4 rounded-xl border border-[#E5E7EB] text-[14px] font-medium hover:bg-[#F3F4F6]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              data-testid="close-shift-submit"
              className="h-11 px-5 rounded-xl bg-[#121216] text-white text-[14px] font-semibold hover:bg-[#000000] disabled:opacity-60"
            >
              {busy ? "Closing…" : "Close shift"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// — Past shift row
function VarianceBadge({ value, testId }) {
  const v = Number(value || 0);
  return (
    <span
      data-testid={testId}
      className={`inline-block px-2 py-1 rounded-md font-semibold text-[12px] ${
        Math.abs(v) < 0.005 ? "bg-[#ECFDF5] text-[#0F9D58]" : v > 0 ? "bg-[#FEF3C7] text-[#92400E]" : "bg-[#FEE2E2] text-[#DC2626]"
      }`}
    >
      {v >= 0 ? "+" : "−"}${money(Math.abs(v))}
    </span>
  );
}

function PastShiftRow({ shift }) {
  const ov = Number(shift.opening_variance || 0);
  const hasOpening = Math.abs(ov) >= 0.005;
  const total = shift.total_variance ?? Number(shift.variance || 0) + ov;
  const opened = fmtDateTime(shift.opened_at);
  const closed = fmtDateTime(shift.closed_at);
  const sales = Number(shift.totals?.sales_total || 0);
  return (
    <div className={`px-4 py-3 border-b border-[#F3F4F6] text-[13px] ${hasOpening ? "bg-[#FEF2F2]/60 hover:bg-[#FEF2F2]" : "hover:bg-[#F8FAFC]"}`} data-testid={`past-shift-${shift.id}`}>
      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-3 font-mono text-[12px]">
          <div className="text-[#121216] font-semibold">{opened}</div>
          <div className="text-[#9CA3AF]">→ {closed}</div>
        </div>
        <div className="col-span-3">
          <div className="font-medium">{shift.opened_by_name}</div>
          <div className="text-[11px] text-[#9CA3AF]">closed by {shift.closed_by_name || "—"}</div>
        </div>
        <div className="col-span-2 text-right tabular-nums">
          <div className="font-semibold">${money(sales)}</div>
          <div className="text-[11px] text-[#9CA3AF]">{shift.totals?.sale_count || 0} sales</div>
        </div>
        <div className="col-span-2 text-right tabular-nums font-mono text-[12px]">
          {shift.expected_opening != null && (
            <div className={hasOpening ? "text-[#DC2626] font-semibold" : "text-[#9CA3AF]"} data-testid={`past-shift-opening-${shift.id}`}>
              ${money(shift.opening_float)} opened / ${money(shift.expected_opening)} left
            </div>
          )}
          <div>${money(shift.expected_cash)} expected</div>
          <div className="text-[#9CA3AF]">${money(shift.counted_cash)} counted</div>
        </div>
        <div className="col-span-2 text-right tabular-nums font-mono space-y-1">
          <VarianceBadge value={total} testId={`past-shift-total-variance-${shift.id}`} />
          {hasOpening && (
            <div className="text-[10px] text-[#9CA3AF]">open {ov >= 0 ? "+" : "−"}${money(Math.abs(ov))} · close {Number(shift.variance || 0) >= 0 ? "+" : "−"}${money(Math.abs(Number(shift.variance || 0)))}</div>
          )}
        </div>
      </div>
      {hasOpening && (
        <div className="mt-2 flex items-start gap-2 rounded-lg border border-[#FCA5A5] bg-white px-3 py-2 text-[12px] text-[#7F1D1D]" data-testid={`past-shift-opening-alert-${shift.id}`}>
          <span className="font-bold text-[#B91C1C] shrink-0">Opening {ov < 0 ? "short" : "over"} ${money(Math.abs(ov))}</span>
          <span>· {shift.previous_closed_by_name || "Previous shift"} left ${money(shift.expected_opening)}, {shift.opened_by_name} counted ${money(shift.opening_float)}
            {shift.opening_variance_reason ? ` · “${shift.opening_variance_reason}”` : ""}</span>
        </div>
      )}
    </div>
  );
}

export default function Till() {
  const { user } = useAuth();
  const canViewOwnHistory = userCan(user, "till.cashouts.view_own");
  const [active, setActive] = useState(null);
  const [totals, setTotals] = useState(null);
  const [expectedCash, setExpectedCash] = useState(0);
  const [past, setPast] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDlg, setOpenDlg] = useState(false);
  const [cashOutDlg, setCashOutDlg] = useState(false);
  const [cashInDlg, setCashInDlg] = useState(false);
  const [closeDlg, setCloseDlg] = useState(false);
  const [historyDrawer, setHistoryDrawer] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [aRes, hRes] = await Promise.all([
        api.get("/shifts/active"),
        api.get("/shifts?limit=10"),
      ]);
      setActive(aRes.data?.shift || null);
      setTotals(aRes.data?.totals || null);
      setExpectedCash(Number(aRes.data?.expected_cash || 0));
      setPast((hRes.data?.items || []).filter((s) => s.status === "closed"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Auto-refresh active shift totals every 30s while a shift is open.
  useEffect(() => {
    if (!active) return;
    const t = setInterval(refresh, 30000);
    return () => clearInterval(t);
  }, [active, refresh]);

  const cashIn = Number(totals?.by_method?.cash || 0);
  const cardIn = Number(totals?.by_method?.card_manual || 0);
  const tyroIn = Number(totals?.by_method?.tyro || 0);
  const otherIn = Number(totals?.by_method?.other || 0);
  const cashOut = Number(totals?.cash_out_total || 0);
  const cashInInjected = Number(totals?.cash_in_total || 0);
  // Per-method refund breakdown — only the CASH bucket actually leaves
  // the drawer. Card / Tyro refunds go back via the acquirer and never
  // touch physical cash. Surfacing this prevents staff from thinking a
  // card refund "stole" $X from the drawer.
  const cashRefund = Number(totals?.refund_by_method?.cash || 0);
  const cardRefund = Number(totals?.refund_by_method?.card_manual || 0);
  const tyroRefund = Number(totals?.refund_by_method?.tyro || 0);
  const nonCashRefund = Math.round((cardRefund + tyroRefund) * 100) / 100;

  return (
    <Layout>
      <PageHeader
        overline="TILL"
        title="Cash drawer"
        actions={
          <>
            {canViewOwnHistory && (
              <button
                onClick={() => setHistoryDrawer(true)}
                data-testid="till-my-history-btn"
                className="inline-flex items-center gap-2 h-11 px-4 rounded-xl border border-[#E5E7EB] bg-white hover:border-[#92400E] hover:text-[#92400E] text-[13px] font-semibold"
              >
                <ClockCounterClockwise size={14} weight="bold" />
                My cash-out history
              </button>
            )}
            {active ? (
              <>
                <button
                  onClick={() => setCashInDlg(true)}
                  data-testid="till-cash-in-btn"
                  className="inline-flex items-center gap-2 h-11 px-4 rounded-xl border border-[#E5E7EB] bg-white hover:border-[#0F9D58] hover:text-[#0F9D58] text-[13px] font-semibold"
                >
                  <ArrowDownLeft size={14} weight="bold" />
                  Cash in
                </button>
                <button
                  onClick={() => setCashOutDlg(true)}
                  data-testid="till-cash-out-btn"
                  className="inline-flex items-center gap-2 h-11 px-4 rounded-xl border border-[#E5E7EB] bg-white hover:border-[#DC2626] hover:text-[#DC2626] text-[13px] font-semibold"
                >
                  <ArrowUpRight size={14} weight="bold" />
                  Cash out
                </button>
                <button
                  onClick={() => setCloseDlg(true)}
                  data-testid="till-close-shift-btn"
                  className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-[#121216] text-white hover:bg-black text-[13px] font-semibold"
                >
                  <Lock size={14} weight="bold" />
                  End shift
                </button>
              </>
            ) : (
              <button
                onClick={() => setOpenDlg(true)}
                data-testid="till-open-shift-btn"
                className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-[#1E5FEB] text-white hover:bg-[#1849C8] text-[13px] font-semibold"
              >
                <LockOpen size={14} weight="bold" />
                Open shift
              </button>
            )}
          </>
        }
      />

      <div className="px-5 md:px-6 lg:px-10 pb-12">
        {loading ? (
          <div className="text-[14px] text-[#9CA3AF] py-8">Loading…</div>
        ) : !active ? (
          <div className="rounded-2xl border-2 border-dashed border-[#E5E7EB] p-10 text-center" data-testid="till-no-shift">
            <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-[#DBEAFE] text-[#1E5FEB] flex items-center justify-center">
              <Wallet size={26} weight="duotone" />
            </div>
            <div className="font-display font-semibold text-[18px] mb-1">No shift in progress</div>
            <div className="text-[13px] text-[#4B5563] max-w-md mx-auto">
              Count the cash currently in the drawer and tap{" "}
              <span className="font-semibold text-[#1E5FEB]">Open shift</span> to start tracking sales,
              refunds, and cash-outs.
            </div>
            <button
              onClick={() => setOpenDlg(true)}
              className="mt-5 inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#1E5FEB] text-white hover:bg-[#1849C8] text-[14px] font-semibold"
            >
              <LockOpen size={14} weight="bold" />
              Open shift
            </button>

            {past.length > 0 && (
              <div className="mt-10 text-left">
                <div className="text-[10px] uppercase tracking-[0.16em] text-[#9CA3AF] font-medium mb-2">
                  Recent shifts
                </div>
                <PastShiftsTable rows={past} />
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Active shift hero */}
            <div
              className="rounded-3xl bg-white border border-[#E5E7EB] p-6 mb-6 relative overflow-hidden"
              style={{ boxShadow: "0 4px 20px rgba(36,71,55,0.06)" }}
              data-testid="till-active-card"
            >
              <div
                className="absolute -top-20 -right-20 w-72 h-72 rounded-full"
                style={{
                  background: "radial-gradient(circle, rgba(30,95,235,0.08), transparent 70%)",
                }}
              />
              <div className="flex items-start justify-between gap-4 mb-5 relative">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#1E5FEB] font-bold mb-1.5 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-[#0F9D58] animate-pulse" />
                    Shift in progress
                  </div>
                  <div className="font-display font-semibold text-[26px] leading-tight">
                    {active.opened_by_name}
                  </div>
                  <div className="text-[12px] text-[#9CA3AF] mt-0.5">
                    Opened {fmtDateTime(active.opened_at)}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-[#9CA3AF] font-medium">Expected cash</div>
                  <div
                    data-testid="till-expected-cash"
                    className="font-display font-semibold text-[36px] tabular-nums leading-none mt-1"
                    style={{ color: "#1E5FEB" }}
                  >
                    ${money(expectedCash)}
                  </div>
                  <div className="text-[11px] text-[#9CA3AF] mt-1 font-mono">
                    Float ${money(active.opening_float)} + cash in + cash injected − cash refunds − cash out
                  </div>
                </div>
              </div>
              <div className={`grid grid-cols-2 ${
                cashInInjected > 0 && tyroIn > 0 ? "lg:grid-cols-7"
                : cashInInjected > 0 || tyroIn > 0 ? "lg:grid-cols-6"
                : "lg:grid-cols-5"
              } gap-3 relative`}>
                <StatTile testid="till-cash-sales" label="Cash in" value={cashIn} accent="#0F9D58" icon={Money} />
                <StatTile testid="till-card-sales" label="Card in" value={cardIn} icon={CreditCard} />
                {tyroIn > 0 && (
                  <StatTile testid="till-tyro-sales" label="EFTPOS · Tyro" value={tyroIn} icon={CreditCard} />
                )}
                <StatTile testid="till-other-sales" label="Other in" value={otherIn} icon={Receipt} />
                {cashInInjected > 0 && (
                  <StatTile
                    testid="till-cash-injected"
                    label="Cash injected"
                    value={cashInInjected}
                    accent="#0F9D58"
                    icon={ArrowDownLeft}
                    sub="Bank / owner top-up"
                  />
                )}
                <StatTile
                  testid="till-refunds"
                  label="Cash refunds"
                  value={cashRefund}
                  accent="#DC2626"
                  icon={ArrowDownRight}
                  sub={nonCashRefund > 0 ? `+ $${money(nonCashRefund)} card/Tyro (not in drawer)` : null}
                />
                <StatTile testid="till-cash-out" label="Cash out" value={cashOut} accent="#DC2626" icon={ArrowUpRight} />
                {(Number(totals?.store_credit_issued || 0) > 0 || Number(totals?.store_credit_used || 0) > 0) && (
                  <StatTile
                    testid="till-store-credit"
                    label="Store credit"
                    value={Number(totals?.store_credit_issued || 0)}
                    accent="#047857"
                    icon={WalletIcon}
                    sub={`Issued · $${money(totals?.store_credit_used || 0)} used · not in drawer`}
                  />
                )}
              </div>
            </div>

            {/* Cash-out log */}
            <div className="rounded-2xl bg-white border border-[#E5E7EB] mb-6 overflow-hidden" data-testid="till-cashouts-card">
              <div className="px-5 py-3 border-b border-[#F3F4F6] flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ListBullets size={16} className="text-[#4B5563]" />
                  <div className="font-semibold text-[14px]">Cash-out log</div>
                  <span className="text-[11px] text-[#9CA3AF]">{(totals?.cash_outs || []).length} entries</span>
                </div>
                <button
                  onClick={() => setCashOutDlg(true)}
                  data-testid="till-add-cashout"
                  className="inline-flex items-center gap-1 h-8 px-3 rounded-lg text-[12px] font-semibold text-[#DC2626] hover:bg-[#FEE2E2]"
                >
                  <Plus size={12} weight="bold" /> New
                </button>
              </div>
              {(totals?.cash_outs || []).length === 0 ? (
                <div className="p-6 text-center text-[13px] text-[#9CA3AF]">No cash-outs yet this shift.</div>
              ) : (
                <div className="divide-y divide-[#F3F4F6]">
                  {totals.cash_outs.map((c) => (
                    <div key={c.id} className="px-5 py-3 flex items-center gap-3 text-[13px] hover:bg-[#F8FAFC]">
                      <div className="w-8 h-8 rounded-lg bg-[#FEE2E2] text-[#DC2626] flex items-center justify-center shrink-0">
                        <ArrowUpRight size={14} weight="bold" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate">{c.category}</div>
                        {c.reason && <div className="text-[11px] text-[#9CA3AF] truncate">{c.reason}</div>}
                      </div>
                      <div className="text-[11px] text-[#9CA3AF] font-mono">
                        {fmtTime(c.created_at)} · {c.created_by_name}
                      </div>
                      <div className="font-mono font-semibold text-[#DC2626] tabular-nums">${money(c.amount)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <PastShiftsTable rows={past} />
          </>
        )}
      </div>

      {openDlg && (
        <OpenShiftDialog
          onClose={() => setOpenDlg(false)}
          onOpened={() => { setOpenDlg(false); refresh(); }}
        />
      )}
      {cashOutDlg && (
        <CashOutDialog
          onClose={() => setCashOutDlg(false)}
          onSaved={() => { setCashOutDlg(false); refresh(); }}
        />
      )}
      {cashInDlg && (
        <CashInDialog
          onClose={() => setCashInDlg(false)}
          onSaved={() => { setCashInDlg(false); refresh(); }}
        />
      )}
      {closeDlg && active && (
        <CloseShiftDialog
          active={active}
          expectedCash={expectedCash}
          onClose={() => setCloseDlg(false)}
          onClosed={() => { setCloseDlg(false); refresh(); }}
        />
      )}
      <MyCashOutsDrawer
        open={historyDrawer}
        onClose={() => setHistoryDrawer(false)}
      />
    </Layout>
  );
}

function PastShiftsTable({ rows }) {
  if (!rows?.length) return null;
  return (
    <div className="rounded-2xl bg-white border border-[#E5E7EB] overflow-hidden" data-testid="till-past-shifts">
      <div className="px-5 py-3 border-b border-[#F3F4F6]">
        <div className="font-semibold text-[14px]">Recent shifts</div>
        <div className="text-[11px] text-[#9CA3AF]">Last {rows.length} closed shifts</div>
      </div>
      <div className="grid grid-cols-12 gap-3 px-4 py-2 border-b border-[#F3F4F6] text-[10px] uppercase tracking-[0.12em] text-[#9CA3AF] font-medium bg-[#F8FAFC]">
        <div className="col-span-3">Window</div>
        <div className="col-span-3">Cashier</div>
        <div className="col-span-2 text-right">Sales</div>
        <div className="col-span-2 text-right">Cash count</div>
        <div className="col-span-2 text-right">Variance</div>
      </div>
      <div>
        {rows.map((s) => <PastShiftRow key={s.id} shift={s} />)}
      </div>
    </div>
  );
}
